import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PhoneIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { CrmRdv, VisiteDetail } from './types';
import { getErrorMessage, getRdvs, getVisiteDetail } from './adminApi';
import { CallActivityList } from './CallActivityList';
import { StatusBadge, VISITE_NIVEAUX, formatRdvDate, statusLabel } from './crmShared';

// ── Status colours (calendar-local: distinguishes "pris" from "confirmé") ──────
const RDV_STATUS_META: Record<string, { label: string; legend: string; pill: string }> = {
  rdv_pris: {
    label: 'RDV pris',
    legend: 'bg-blue-500',
    pill: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 hover:bg-blue-100',
  },
  rdv_confirme: {
    label: 'Confirmé',
    legend: 'bg-emerald-500',
    pill: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100',
  },
  rdv_passe: {
    label: 'Passé',
    legend: 'bg-indigo-500',
    pill: 'bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-100',
  },
  rdv_no_show: {
    label: 'Manqué',
    legend: 'bg-amber-500',
    pill: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200 hover:bg-amber-100',
  },
};
const RDV_STATUS_ORDER = ['rdv_pris', 'rdv_confirme', 'rdv_passe', 'rdv_no_show'];
const pillClass = (status: string) => RDV_STATUS_META[status]?.pill ?? 'bg-gray-100 text-gray-700 ring-1 ring-inset ring-gray-200';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
// Week-view hour rows (school opens ~8h30, closes ~16h30 — keep a little margin).
const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

type ViewMode = 'month' | 'week' | 'agenda';

// ── Date helpers — rdvDate is stored at UTC midnight, so every day-key/label is
// computed with UTC parts to keep the stored calendar day from ever shifting. ──
const dayKey = (d: Date) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
const rdvDayKey = (iso: string) => iso.slice(0, 10);
const keyToDate = (key: string) => new Date(`${key}T00:00:00.000Z`);
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86_400_000);
const startOfMonth = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
const addMonths = (d: Date, n: number) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
const startOfWeek = (d: Date) => addDays(d, -((d.getUTCDay() + 6) % 7)); // Monday on/before

// Today in Casablanca as a YYYY-MM-DD key (matches the backend's date bucketing).
const todayKey = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Casablanca',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

function monthGridKeys(anchor: Date): string[] {
  const start = startOfWeek(startOfMonth(anchor));
  return Array.from({ length: 42 }, (_, i) => dayKey(addDays(start, i)));
}
function weekKeys(anchor: Date): string[] {
  const start = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => dayKey(addDays(start, i)));
}

const MONTH_LABEL = new Intl.DateTimeFormat('fr-MA', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const DAY_SHORT = new Intl.DateTimeFormat('fr-MA', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const DAY_LONG = new Intl.DateTimeFormat('fr-MA', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function bucketHour(r: CrmRdv): number | null {
  if (!r.rdvHeure) return null;
  const h = Number(r.rdvHeure.slice(0, 2));
  if (Number.isNaN(h)) return null;
  return Math.min(HOURS[HOURS.length - 1], Math.max(HOURS[0], h));
}

// ─────────────────────────────────────────────────────────────────────────────

function RdvCalendarPage() {
  const [view, setView] = useState<ViewMode>('month');
  const [anchor, setAnchor] = useState<Date>(() => keyToDate(todayKey()));
  const [rdvs, setRdvs] = useState<CrmRdv[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [niveau, setNiveau] = useState('');
  const [activeStatuses, setActiveStatuses] = useState<Set<string>>(() => new Set(RDV_STATUS_ORDER));

  const today = todayKey();

  // Fetch window depends on the active view + anchored month/week.
  const range = useMemo(() => {
    if (view === 'week') {
      const wk = weekKeys(anchor);
      return { from: wk[0], to: wk[6] };
    }
    if (view === 'month') {
      const grid = monthGridKeys(anchor);
      return { from: grid[0], to: grid[41] };
    }
    const last = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() + 1, 0));
    return { from: dayKey(startOfMonth(anchor)), to: dayKey(last) };
  }, [view, anchor]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    setError('');
    getRdvs(range.from, range.to)
      .then((data) => { if (mounted) setRdvs(data); })
      .catch((err) => { if (mounted) setError(getErrorMessage(err)); })
      .finally(() => { if (mounted) setIsLoading(false); });
    return () => { mounted = false; };
  }, [range.from, range.to]);

  const filtered = useMemo(
    () => rdvs.filter((r) => (!niveau || r.niveau === niveau) && activeStatuses.has(r.status)),
    [rdvs, niveau, activeStatuses]
  );

  const byDay = useMemo(() => {
    const m = new Map<string, CrmRdv[]>();
    for (const r of filtered) {
      const k = rdvDayKey(r.rdvDate);
      const arr = m.get(k);
      if (arr) arr.push(r);
      else m.set(k, [r]);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => (a.rdvHeure ?? '99:99').localeCompare(b.rdvHeure ?? '99:99'));
    }
    return m;
  }, [filtered]);

  const stats = useMemo(() => {
    let todayCount = 0;
    let upcoming = 0;
    for (const r of filtered) {
      const k = rdvDayKey(r.rdvDate);
      if (k === today) todayCount += 1;
      if (k >= today) upcoming += 1;
    }
    return { total: filtered.length, today: todayCount, upcoming };
  }, [filtered, today]);

  const headerLabel = useMemo(() => {
    if (view === 'week') {
      const wk = weekKeys(anchor);
      return `${DAY_SHORT.format(keyToDate(wk[0]))} – ${DAY_SHORT.format(keyToDate(wk[6]))}`;
    }
    return cap(MONTH_LABEL.format(startOfMonth(anchor)));
  }, [view, anchor]);

  const step = (dir: -1 | 1) =>
    setAnchor((cur) => (view === 'week' ? addDays(cur, 7 * dir) : addMonths(cur, dir)));
  const goToday = () => setAnchor(keyToDate(today));

  const toggleStatus = (status: string) =>
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });

  const viewButton = (mode: ViewMode, label: string) => (
    <button
      type="button"
      onClick={() => setView(mode)}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        view === mode ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="px-4 py-6 lg:px-8">
      {/* Title + stats */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-950">Calendrier des rendez-vous</h2>
          <p className="mt-1 text-sm text-gray-500">Tous les RDV de visite — partagé par l’équipe.</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Affichés', value: stats.total },
            { label: "Aujourd'hui", value: stats.today },
            { label: 'À venir', value: stats.upcoming },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-center shadow-sm">
              <p className="text-xl font-bold text-gray-950">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => step(-1)}
            className="rounded-md border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-50"
            aria-label="Précédent"
          >
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => step(1)}
            className="rounded-md border border-gray-300 bg-white p-2 text-gray-600 hover:bg-gray-50"
            aria-label="Suivant"
          >
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={goToday}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Aujourd'hui
          </button>
          <h3 className="ml-2 min-w-0 truncate text-lg font-semibold text-gray-900">{headerLabel}</h3>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select
            value={niveau}
            onChange={(e) => setNiveau(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            aria-label="Filtrer par niveau"
          >
            <option value="">Tous les niveaux</option>
            {VISITE_NIVEAUX.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <div className="inline-flex rounded-lg bg-gray-100 p-1">
            {viewButton('month', 'Mois')}
            {viewButton('week', 'Semaine')}
            {viewButton('agenda', 'Agenda')}
          </div>
        </div>
      </div>

      {/* Status legend = filter */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {RDV_STATUS_ORDER.map((status) => {
          const meta = RDV_STATUS_META[status];
          const on = activeStatuses.has(status);
          return (
            <button
              key={status}
              type="button"
              onClick={() => toggleStatus(status)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                on ? 'border-gray-300 bg-white text-gray-700' : 'border-gray-200 bg-gray-50 text-gray-400'
              }`}
              aria-pressed={on}
            >
              <span className={`h-2.5 w-2.5 rounded-full ${on ? meta.legend : 'bg-gray-300'}`} aria-hidden="true" />
              {meta.label}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading ? (
          <div className="py-20 text-center text-sm text-gray-500">Chargement…</div>
        ) : view === 'month' ? (
          <MonthView
            gridKeys={monthGridKeys(anchor)}
            monthStr={dayKey(startOfMonth(anchor)).slice(0, 7)}
            byDay={byDay}
            today={today}
            onSelect={setSelectedId}
            onOverflow={() => setView('agenda')}
          />
        ) : view === 'week' ? (
          <WeekView keys={weekKeys(anchor)} byDay={byDay} today={today} onSelect={setSelectedId} />
        ) : (
          <AgendaView
            monthStr={dayKey(startOfMonth(anchor)).slice(0, 7)}
            byDay={byDay}
            today={today}
            onSelect={setSelectedId}
          />
        )}
      </div>

      <AnimatePresence>
        {selectedId && <DetailDrawer key="rdv-drawer" id={selectedId} onClose={() => setSelectedId(null)} />}
      </AnimatePresence>
    </div>
  );
}

// ── Month view ───────────────────────────────────────────────────────────────
function MonthView({
  gridKeys,
  monthStr,
  byDay,
  today,
  onSelect,
  onOverflow,
}: {
  gridKeys: string[];
  monthStr: string;
  byDay: Map<string, CrmRdv[]>;
  today: string;
  onSelect: (id: string) => void;
  onOverflow: () => void;
}) {
  return (
    <div>
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 text-center text-xs font-semibold uppercase tracking-wide text-gray-500">
        {WEEKDAYS.map((d) => (
          <div key={d} className="py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {gridKeys.map((key) => {
          const items = byDay.get(key) ?? [];
          const inMonth = key.slice(0, 7) === monthStr;
          const isToday = key === today;
          const shown = items.slice(0, 3);
          const extra = items.length - shown.length;
          return (
            <div
              key={key}
              className={`min-h-[96px] border-b border-r border-gray-100 p-1.5 last:border-r-0 ${
                inMonth ? 'bg-white' : 'bg-gray-50/50'
              }`}
            >
              <div className="mb-1 flex justify-end">
                <span
                  className={`flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1 text-xs font-semibold ${
                    isToday
                      ? 'bg-primary text-white'
                      : inMonth
                        ? 'text-gray-700'
                        : 'text-gray-300'
                  }`}
                >
                  {Number(key.slice(8, 10))}
                </span>
              </div>
              <div className="space-y-1">
                {shown.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => onSelect(r.id)}
                    className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-medium ${pillClass(r.status)}`}
                    title={`${r.rdvHeure ?? ''} ${r.parentName} · ${r.niveau}`}
                  >
                    {r.rdvHeure && <span className="tabular-nums">{r.rdvHeure} </span>}
                    {r.parentName}
                  </button>
                ))}
                {extra > 0 && (
                  <button
                    type="button"
                    onClick={onOverflow}
                    className="block w-full rounded px-1.5 text-left text-[11px] font-medium text-gray-500 hover:text-gray-800"
                  >
                    +{extra} autre{extra > 1 ? 's' : ''}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Week view ────────────────────────────────────────────────────────────────
function WeekView({
  keys,
  byDay,
  today,
  onSelect,
}: {
  keys: string[];
  byDay: Map<string, CrmRdv[]>;
  today: string;
  onSelect: (id: string) => void;
}) {
  const cols = '56px repeat(7, minmax(0, 1fr))';
  const hasUnscheduled = keys.some((k) => (byDay.get(k) ?? []).some((r) => !r.rdvHeure));

  const cell = (r: CrmRdv) => (
    <button
      key={r.id}
      type="button"
      onClick={() => onSelect(r.id)}
      className={`mb-1 block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-medium ${pillClass(r.status)}`}
      title={`${r.rdvHeure ?? ''} ${r.parentName} · ${r.niveau}`}
    >
      {r.rdvHeure && <span className="tabular-nums">{r.rdvHeure} </span>}
      {r.parentName}
    </button>
  );

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[760px]">
        {/* Day headers */}
        <div className="grid border-b border-gray-200 bg-gray-50" style={{ gridTemplateColumns: cols }}>
          <div />
          {keys.map((k, i) => {
            const isToday = k === today;
            return (
              <div key={k} className="border-l border-gray-100 py-2 text-center">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{WEEKDAYS[i]}</p>
                <p
                  className={`mx-auto mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold ${
                    isToday ? 'bg-primary text-white' : 'text-gray-800'
                  }`}
                >
                  {Number(k.slice(8, 10))}
                </p>
              </div>
            );
          })}
        </div>

        {/* Unscheduled (no time) strip */}
        {hasUnscheduled && (
          <div className="grid border-b border-gray-100 bg-amber-50/30" style={{ gridTemplateColumns: cols }}>
            <div className="px-1 py-1.5 text-right text-[10px] font-medium uppercase text-gray-400">heure ?</div>
            {keys.map((k) => (
              <div key={k} className="border-l border-gray-100 p-1">
                {(byDay.get(k) ?? []).filter((r) => !r.rdvHeure).map(cell)}
              </div>
            ))}
          </div>
        )}

        {/* Hour rows */}
        {HOURS.map((h) => (
          <div key={h} className="grid border-b border-gray-100 last:border-b-0" style={{ gridTemplateColumns: cols }}>
            <div className="px-1 py-1.5 text-right text-[11px] tabular-nums text-gray-400">
              {String(h).padStart(2, '0')}:00
            </div>
            {keys.map((k) => (
              <div key={k} className="min-h-[44px] border-l border-gray-100 p-1">
                {(byDay.get(k) ?? []).filter((r) => bucketHour(r) === h).map(cell)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Agenda view ──────────────────────────────────────────────────────────────
function AgendaView({
  monthStr,
  byDay,
  today,
  onSelect,
}: {
  monthStr: string;
  byDay: Map<string, CrmRdv[]>;
  today: string;
  onSelect: (id: string) => void;
}) {
  const dayKeys = [...byDay.keys()].filter((k) => k.slice(0, 7) === monthStr).sort();

  if (dayKeys.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-20 text-center text-gray-400">
        <CalendarDaysIcon className="h-10 w-10" aria-hidden="true" />
        <p className="text-sm">Aucun rendez-vous ce mois-ci.</p>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-gray-100">
      {dayKeys.map((key) => {
        const items = byDay.get(key) ?? [];
        const isPast = key < today;
        const isToday = key === today;
        return (
          <li key={key} className={isPast ? 'opacity-70' : ''}>
            <div className="flex items-center justify-between bg-gray-50/60 px-4 py-2">
              <span className="text-sm font-semibold capitalize text-gray-900">{DAY_LONG.format(keyToDate(key))}</span>
              {isToday && (
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">Aujourd'hui</span>
              )}
            </div>
            <ul className="divide-y divide-gray-50">
              {items.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(r.id)}
                    className="flex w-full items-center gap-4 px-4 py-3 text-left hover:bg-gray-50"
                  >
                    <span className="w-12 shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                      {r.rdvHeure ?? '—'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-gray-950">{r.parentName}</span>
                      <span className="text-xs text-gray-500">{r.niveau}</span>
                    </span>
                    <StatusBadge status={r.status} />
                  </button>
                </li>
              ))}
            </ul>
          </li>
        );
      })}
    </ul>
  );
}

// ── Detail drawer (read-only) ────────────────────────────────────────────────
function DetailDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const [detail, setDetail] = useState<VisiteDetail | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setDetail(null);
    setError('');
    getVisiteDetail(id)
      .then((d) => { if (mounted) setDetail(d); })
      .catch((err) => { if (mounted) setError(getErrorMessage(err)); });
    return () => { mounted = false; };
  }, [id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fields = detail
    ? [
        { label: 'Niveau', value: detail.niveau },
        { label: 'Quartier', value: detail.quartier },
        { label: "Nombre d'enfants", value: String(detail.nombreEnfants) },
        { label: 'Email', value: detail.email || '—' },
        { label: 'Origine', value: detail.source === 'meta_native' ? 'Meta Lead' : 'Site web' },
      ]
    : [];

  return (
    <motion.div
      className="fixed inset-0 z-30 flex justify-end bg-gray-950/30"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.aside
        className="flex h-full w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-xl"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.25 }}
        role="dialog"
        aria-label="Détail du rendez-vous"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            {detail ? (
              <>
                <h3 className="break-words text-lg font-bold text-gray-950">{detail.parentName}</h3>
                <div className="mt-1"><StatusBadge status={detail.status} /></div>
              </>
            ) : (
              <h3 className="text-lg font-bold text-gray-400">Chargement…</h3>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Fermer"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}
          {detail && (
            <>
              {/* RDV highlight */}
              <div className="mb-5 rounded-lg border border-blue-100 bg-blue-50/60 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Rendez-vous</p>
                <p className="mt-1 text-sm font-semibold capitalize text-gray-900">
                  {detail.rdvDate ? formatRdvDate(detail.rdvDate) : 'Date non précisée'}
                </p>
                {detail.rdvHeure && <p className="text-sm text-gray-700">à {detail.rdvHeure}</p>}
              </div>

              {/* Phone (click to call) */}
              <a
                href={`tel:${detail.phone}`}
                className="mb-5 flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 hover:bg-gray-50"
              >
                <PhoneIcon className="h-5 w-5 text-primary" aria-hidden="true" />
                {detail.phone}
              </a>

              <dl className="grid grid-cols-2 gap-4">
                {fields.map(({ label, value }) => (
                  <div key={label}>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
                    <dd className="mt-1 break-words text-sm text-gray-900">{value}</dd>
                  </div>
                ))}
              </dl>

              {detail.message && (
                <div className="mt-4">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Message</dt>
                  <dd className="mt-1 break-words text-sm text-gray-900">{detail.message}</dd>
                </div>
              )}

              <div className="mt-6 border-t border-gray-100 pt-4">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Historique des appels</p>
                <CallActivityList activities={detail.callActivities} />
              </div>
            </>
          )}
        </div>

        <div className="border-t border-gray-200 px-5 py-3 text-xs text-gray-400">
          {detail ? `Statut : ${statusLabel(detail.status)} · lecture seule` : ''}
        </div>
      </motion.aside>
    </motion.div>
  );
}

export default RdvCalendarPage;
